import moment from 'moment';

/**
 * Filter dates are calendar bounds in the user's local timezone. Convert the local
 * start/end instants to RFC3339 UTC — do not use startOf/endOf in UTC on the raw
 * timestamp or the calendar day shifts (e.g. Europe/Madrid).
 */
function dateRangeToB4Params(range, initKey, endKey) {
    if (!range) return {};
    const from = range.from ?? range.min;
    const to = range.to ?? range.max;
    const params = {};
    if (from) {
        params[initKey] = moment(from).startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    }
    if (to) {
        params[endKey] = moment(to).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    }
    return params;
}

function normalizeStatusIds(value) {
    if (value == null) return [];
    const arr = Array.isArray(value) ? value : [value];
    return arr
        .map((v) => (v && typeof v === 'object' ? (v.value ?? v['-id'] ?? v.id) : v))
        .map((v) => (typeof v === 'number' ? v : parseInt(v, 10)))
        .filter((n) => Number.isFinite(n) && !Number.isNaN(n));
}

/** Fuzzy list selections (e.g. usuarios): ids for query string. */
function normalizeFuzzyEntityIds(value) {
    if (value == null) return [];
    const arr = Array.isArray(value) ? value : [value];
    return arr
        .map((v) => {
            if (v == null) return null;
            if (typeof v === 'object') {
                return v.Id ?? v['-id'] ?? v.id ?? v.value;
            }
            return v;
        })
        .map((id) => {
            const n = typeof id === 'number' ? id : parseInt(id, 10);
            return Number.isFinite(n) && !Number.isNaN(n) ? n : null;
        })
        .filter((id) => id != null);
}

function setBracketIdArray(params, bracketKey, ids) {
    if (!ids?.length) return;
    params[bracketKey] = ids;
}

/**
 * Maps entity filter state to B4 `mailing/v2/campaign` list query params.
 * Arrays use bracket keys (`status[]`, `updatedUser[]`, …) for repeated query fields.
 */
export function buildMarketingCampaignListQueryParams(filters) {
    const params = {};
    if (!filters) return params;

    Object.keys(filters).forEach((key) => {
        const f = filters[key];
        if (!f || f.invisible || f.isPreload) return;

        const { id, value, dataType } = f;

        if (id === 'matchingName') {
            const q = typeof value === 'string' ? value.trim() : value;
            if (q) params.matchingName = q;
            return;
        }

        if (id === 'status') {
            const ids = normalizeStatusIds(value);
            setBracketIdArray(params, 'status[]', ids);
            return;
        }

        if (id === 'updatedUser') {
            const ids = normalizeFuzzyEntityIds(value);
            setBracketIdArray(params, 'updatedUser[]', ids);
            return;
        }

        if (id === 'createdUser') {
            const ids = normalizeFuzzyEntityIds(value);
            setBracketIdArray(params, 'createdUser[]', ids);
            return;
        }

        if (id === 'dateCreation' && dataType === 'date') {
            const range = f.completeValues || value;
            Object.assign(
                params,
                dateRangeToB4Params(range, 'dateCreationInit', 'dateCreationEnd'),
            );
            return;
        }

        if (id === 'dateModification' && dataType === 'date') {
            const range = f.completeValues || value;
            Object.assign(params, dateRangeToB4Params(range, 'dateUpdateInit', 'dateUpdateEnd'));
            return;
        }

        if (id === 'dateLaunch' && dataType === 'date') {
            const range = f.completeValues || value;
            Object.assign(params, dateRangeToB4Params(range, 'dateLaunchInit', 'dateLaunchEnd'));
        }
    });

    return params;
}
